import { IAppContext } from 'App/AppContext';
import { DocumentProviderType, getListCapabilities } from 'models/documentProvider';
import { IdentityProviderType } from 'models/identityProvider';
import ResourceLink from 'models/resourceLink';
import { apiGetLinkEmbedUrl, apiGetLinkProviderMetadata, apiGetLinkWebUrl } from 'services/Api/linkContentService';
import { apiRequest } from 'services/Auth/authConfig';
import logger from 'services/Logging/logService';
import AppError from 'utils/appError';
import { ControllerExceptionCodes } from 'utils/controllerException';

/**
 * The same content questions as sharePointLinkContent, answered through the API broker for a
 * category on Google Drive, Nextcloud, Notion or Loop. The credentials live on the server, and
 * neither Notion nor a customer's Nextcloud would answer a cross-origin browser call anyway.
 *
 * Operations these providers do not have return the empty answer rather than throwing: the
 * capabilities already keep the UI from offering them, and a thrown error here would turn a
 * missing feature into a red bar.
 */

export async function getWebURLBrokered(link: ResourceLink, appContext: IAppContext): Promise<string> {
  if (link.isWholeList() && link.list) {
    return link.list.webURL || '';
  }

  try {
    const accessToken = await appContext.getAccessToken(apiRequest.scopes);
    const url = await apiGetLinkWebUrl(link.linkId, accessToken);

    if (url) {
      let result = url;
      if (link.linkURLFragment && link.linkURLFragment.trim() !== '') {
        result += link.linkURLFragment;
      }

      return result;
    }
  } catch (err) {
    logger.debug('Error while getting the provider web url', err);
  }

  //the stored url is what the item was linked with, so it is the honest fallback
  return link.linkURL;
}

/**
 * The provider's own preview page, when the stage offers it (the embedPreview capability, today
 * Google Drive's embed page) and the signed-in person has a session at that provider the page can
 * use: the API applies the same rule and refuses everyone else by code, so nothing is asked for them.
 */
export async function getEmbedURLBrokered(link: ResourceLink, appContext: IAppContext): Promise<string | undefined> {
  if (link.isWholeList() || !link.list) return undefined;

  const capabilities = getListCapabilities(link.list, appContext.globalDataCache);

  if (!capabilities.embedPreview || !hasSessionAtProvider(link.list.providerType, appContext)) return undefined;

  try {
    const accessToken = await appContext.getAccessToken(apiRequest.scopes);

    return (await apiGetLinkEmbedUrl(link.linkId, accessToken)) || undefined;
  } catch (err) {
    logger.debug('Error while getting the provider embed url', err);

    return undefined;
  }
}

/**
 * Whether the browser can be expected to hold a session at the provider: the one case that is
 * known is a person who signed in to ISOPlanner through that provider.
 */
export function hasSessionAtProvider(providerType: DocumentProviderType, appContext: IAppContext): boolean {
  switch (providerType) {
    case DocumentProviderType.GoogleDrive:
      return appContext.user.login.identityProviderType === IdentityProviderType.Google;
    default:
      return false;
  }
}

/** None of the providers expose a thumbnail endpoint we use; thumbnail says so. */
export async function getThumbnailBrokered(): Promise<string> {
  return '';
}

/**
 * Providers have no major/minor version model, so there is no version history to fill. The
 * modified date still comes back, because the library list shows it.
 */
export async function setMetaDataBrokered(link: ResourceLink, appContext: IAppContext): Promise<void> {
  if (link.isWholeList() || !link.list) return;

  try {
    const accessToken = await appContext.getAccessToken(apiRequest.scopes);
    const metadata = await apiGetLinkProviderMetadata(link.linkId, accessToken);

    if (!metadata.hasError && metadata.lastModified) {
      link.versionInfo.date = metadata.lastModified;
    }
  } catch (err) {
    logger.debug('Error while getting the provider meta data', err);
  }
}

/** Publishing rests on SharePoint versioning; the publish capability is false for every provider. */
export async function publishBrokered(): Promise<void> {
  return;
}

/**
 * Validity is a metadata probe against the provider.
 *
 * The batch-shaped endpoint answers 200 with hasError set for an item it could not read, and
 * that flag covers a deleted item as well as an auth failure or a throttle. Those cannot be
 * told apart here, so an unreadable item is reported as UNKNOWN rather than invalid - the same
 * choice the SharePoint path makes for a link in another tenant. Only an explicit not-found
 * answer marks the item gone.
 */
export async function isValidBrokered(link: ResourceLink, appContext: IAppContext): Promise<boolean | undefined> {
  link.isValidated = true;
  if (link.isWholeList()) return true;
  if (!link.list) return false;

  try {
    const accessToken = await appContext.getAccessToken(apiRequest.scopes);
    const metadata = await apiGetLinkProviderMetadata(link.linkId, accessToken);

    return metadata.hasError ? undefined : true;
  } catch (err) {
    // the DOCPROV code sits in the ControllerException the API attached, not in the HTTP
    // status that AppError.code holds
    const code = AppError.getControllerExceptionCode(err);

    if (
      code === ControllerExceptionCodes.Code_DocumentProvider_ItemNotFound ||
      code === ControllerExceptionCodes.Code_General_NotFound
    ) {
      return false;
    }

    logger.debug('Error while validating the provider item', err);

    return undefined;
  }
}
